import * as React from "react";
import { PageContainer } from "@/components/ui/page-container";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";

export interface EventDetailSkeletonProps {
  className?: string;
}

export const EventDetailSkeleton = React.forwardRef<
  HTMLDivElement,
  EventDetailSkeletonProps
>(({ className }, ref) => {
  return (
    <>
      {/* Mobile title */}
      <Skeleton className="my-5 block h-6 w-40 self-center md:hidden" />

      {/* Action buttons */}
      <div className="z-50 mx-6 flex items-center gap-2 md:fixed md:top-26 md:right-10 md:w-auto">
        <Button
          variant="destructive"
          disabled
          className="w-full gap-2 md:w-auto"
        >
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="hidden h-4 w-20 sm:inline lg:inline" />
        </Button>

        <Button variant="primary" disabled className="w-full gap-2 md:w-auto">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="hidden h-4 w-16 sm:inline lg:inline" />
        </Button>
      </div>

      {/* Main content */}
      <PageContainer className="mx-6 mt-2.5">
        <div
          ref={ref}
          className={cn(
            "space-y-8 rounded-2xl p-6 md:space-y-10 md:p-8 lg:p-10",
            className,
          )}
        >
          {/* Title */}
          <Skeleton className="h-8 w-2/3" />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-12">
            {/* Info rows */}
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-48" />
              </div>
            ))}

            {/* Room + Capacity */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-12">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-40" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-28" />
              </div>
            </div>

            {/* Discipline pill */}
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="hidden h-7 w-24 rounded-full md:inline-flex" />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <Skeleton className="h-4 w-32" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
});

EventDetailSkeleton.displayName = "EventDetailSkeleton";
